import { getItemCategoriesData, updateItemCategoriesData } from 'src/service/apiService';

const STALE_MS = 5 * 60 * 1000;

let ITEM_CATEGORIES = [];      // For UI
let BASELINE = null;           // Authoritative normalized baseline from last live GET
let BASELINE_MAP = new Map();  // key -> { catIdx, itemIdx }
let ITEM_CATEGORIES_ETAG = null;

const listeners = new Set();
const STATUS = {
  inFlight: false,
  lastFetchTime: 0,
  lastFetchOk: false,
  error: null,
  fallbackUsed: false
};

/* ---------- Helpers ---------- */
const normVol = (v) => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const extractArray = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.data)) return raw.data;
  return [];
};
const normalizeServer = (raw) => {
  const cats = extractArray(raw);
  return cats.map((c) => {
    const name = typeof c.label === 'string' ? c.label : (c.name || '');
    return {
      id: typeof c.id === 'string' ? c.id : c.key || '',
      name,
      label: name,
      items: Array.isArray(c.items)
        ? c.items.map((it) => ({
            id: it.id || it.key || '',
            key: it.id || it.key || '',
            label: typeof it.label === 'string' ? it.label : it.name || '',
            volume: normVol(it.volume),
          }))
        : [],
    };
  });
};
const clone = (x) => JSON.parse(JSON.stringify(x));
const rebuildBaselineMap = () => {
  BASELINE_MAP.clear();
  BASELINE.forEach((cat, ci) => {
    cat.items.forEach((it, ii) => {
      const k = it.id || it.key;
      if (k) BASELINE_MAP.set(k, { ci, ii });
    });
  });
};
const notify = () => {
  const snap = { ...STATUS };
  [...listeners].forEach(fn => { try { fn(ITEM_CATEGORIES, snap); } catch {} });
};

/* ---------- Public selectors ---------- */
export const subscribeItemCategories = (cb) => { listeners.add(cb); return () => listeners.delete(cb); };
export const getItemCategories = () => ITEM_CATEGORIES;
export const getItemCategoriesStatus = () => ({ ...STATUS });
export const isItemCategoriesLoading = () => STATUS.inFlight;

/* ---------- Apply from server ---------- */
const applyFromServer = (raw, etag) => {
  BASELINE = normalizeServer(raw);
  ITEM_CATEGORIES = clone(BASELINE);
  rebuildBaselineMap();
  if (typeof window !== 'undefined') {
    try { localStorage.setItem('itemCategories', JSON.stringify(ITEM_CATEGORIES)); } catch {}
    if (etag) {
      ITEM_CATEGORIES_ETAG = etag;
      try { localStorage.setItem('itemCategoriesEtag', etag); } catch {}
    }
  }
  notify();
};

/* ---------- Fetch ---------- */
const performFetch = async ({ forceLive, timeoutMs, allowCacheFallback }) => {
  if (STATUS.inFlight) {
    return new Promise(resolve => {
      const unsub = subscribeItemCategories((_c, st) => {
        if (!st.inFlight) {
          unsub();
            resolve({ data: ITEM_CATEGORIES, fromCache: st.fallbackUsed, error: st.error });
        }
      });
    });
  }
  STATUS.inFlight = true;
  STATUS.error = null;
  notify();

  let controller, timer;
  if (typeof AbortController !== 'undefined') {
    controller = new AbortController();
    timer = setTimeout(() => controller.abort(), timeoutMs);
  }

  try {
    const headers =
      ITEM_CATEGORIES_ETAG && !forceLive ? { 'If-None-Match': ITEM_CATEGORIES_ETAG } : {};
    const resp = await getItemCategoriesData({ headers, signal: controller?.signal });

    if (resp.status === 304) {
      STATUS.lastFetchOk = !!ITEM_CATEGORIES.length;
      STATUS.lastFetchTime = Date.now();
      STATUS.inFlight = false;
      notify();
      return { data: ITEM_CATEGORIES, fromCache: true, error: STATUS.lastFetchOk ? null : new Error('No cache + 304') };
    }

    applyFromServer(resp.data, resp.headers?.etag);
    STATUS.lastFetchOk = true;
    STATUS.lastFetchTime = Date.now();
    STATUS.inFlight = false;
    STATUS.fallbackUsed = false;
    notify();
    return { data: ITEM_CATEGORIES, fromCache: false, error: null };
  } catch (e) {
    STATUS.error = e;
    STATUS.lastFetchOk = false;
    STATUS.inFlight = false;
    if (allowCacheFallback && ITEM_CATEGORIES.length) {
      STATUS.fallbackUsed = true;
      notify();
      return { data: ITEM_CATEGORIES, fromCache: true, error: e };
    }
    STATUS.fallbackUsed = false;
    notify();
    return { data: [], fromCache: false, error: e };
  } finally {
    if (timer) clearTimeout(timer);
  }
};

export const loadItemCategories = async ({ forceLive = false, timeoutMs = 6000, allowCacheFallback = true } = {}) => {
  const now = Date.now();
  if (!forceLive && ITEM_CATEGORIES.length && now - STATUS.lastFetchTime < STALE_MS) {
    (async () => { try { await performFetch({ forceLive: false, timeoutMs, allowCacheFallback }); } catch {} })();
    return { data: ITEM_CATEGORIES, fromCache: true, error: null };
  }
  return performFetch({ forceLive, timeoutMs, allowCacheFallback });
};

/* ---------- Save ---------- */
export const updateItemCategoriesStrict = async (categories) => {
  try {
    await updateItemCategoriesData(categories, { headers: {} });
    await loadItemCategories({ forceLive: true });
    return { ok: true };
  } catch (err) {
    let body = err?.response?.data;
    if (body && typeof body !== 'string') {
      try { body = JSON.stringify(body); } catch {}
    }
    throw new Error(body || err.message || 'Save failed');
  }
};

/* ---------- Derived helper ---------- */
export const getTotalVolume = (selected) => {
  if (!selected) return 0;
  const flat = ITEM_CATEGORIES.flatMap((c) => c.items);
  const map = new Map(flat.map((it) => [(it.id || it.key), it.volume]));
  return Object.entries(selected).reduce(
    (sum, [k, qty]) => sum + (map.get(k) || 0) * (qty || 0),
    0
  );
};

/* ---------- Bootstrap (UI convenience only) ---------- */
(function bootstrapFromCache() {
  if (typeof window === 'undefined') return;
  try {
    const stored = localStorage.getItem('itemCategories');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        ITEM_CATEGORIES = parsed.map((c) => ({
          id: c.id || c.key || '',
          name: c.name || c.label || '',
          label: c.name || c.label || '',
          items: (c.items || []).map((it) => ({
            id: it.id || it.key,
            key: it.id || it.key,
            label: it.label,
            volume: normVol(it.volume),
          })),
        }));
        // DO NOT treat this as authoritative baseline (forceFetch handles that)
        notify();
      }
    }
  } catch {}
})();

/* ---------- Cross-tab sync ---------- */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'itemCategories' && e.newValue) {
      try {
        const parsed = JSON.parse(e.newValue);
        if (Array.isArray(parsed)) {
          ITEM_CATEGORIES = parsed;
          notify();
        }
      } catch {}
    }
    if (e.key === 'itemCategoriesEtag') {
      ITEM_CATEGORIES_ETAG = e.newValue;
    }
  });
}
